import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Collapse, Button } from "reactstrap";
import { useAppData } from "../context";
import { LoadChart, BatteryChart, PricesChart, GridChart, SolarChart } from "../components/graphs/Graphs";
import PerformanceOverviewBlock from "../components/PerformanceOverviewBlock";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateCustomSchedule } from "../api/usersApi";
import ConfirmationModal from "../components/ConfirmationModal";
import { DateTime } from "luxon";

const SiteSchedule = ({ site, batteryPower, entsoePrices, carbon_intensity, grid, load, solar, userTimezone }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Col className="px-0">
      <Card className="mb-3 shadow border-lt">
        <CardBody className="p-2 cardbody-md-default">
          <div
            className="d-flex flex-row justify-content-between align-items-center"
            style={{ minHeight: 60 }}
          >
            <div className="flex-fill text-center">
              <h5 className="text-left text-muted">
                <strong>{site.display_name + " - " + t("Geek-corner-advanced-graphs")}</strong>
              </h5>
            </div>
            <div
              className="status-cell status-cell-4 flex-shrink-0 ms-2"
              style={{ minHeight: 60 }}
            >
              <Button
                color="link"
                onClick={() => setIsOpen(!isOpen)}
                className="status-chevron"
                aria-expanded={isOpen}
              >
                {isOpen ? (
                  <ChevronUp data-testid="geek-corner-chevron-up" size={18} />
                ) : (
                  <ChevronDown
                    data-testid="grrk-corner-chevron-down"
                    size={18}
                  />
                )}
              </Button>
            </div>
          </div>
          <Collapse isOpen={isOpen}>
            <div className="details-row">
              <BatteryChart batteryPower={batteryPower} userTimezone={userTimezone} />
              <PricesChart prices={entsoePrices} carbon_intensity={carbon_intensity} userTimezone={userTimezone} chartType="line" />
              <GridChart grid={grid} userTimezone={userTimezone} />
              <LoadChart load={load} userTimezone={userTimezone} />
              <SolarChart solarValue={solar} userTimezone={userTimezone} />
              * {t('Advanced-All times shown in the graphs are in selected timezone.')}
            </div>
          </Collapse>
        </CardBody>
      </Card>
    </Col>
  );
};


const ExpertModeSettings = ({ site, electricalSystem, customPreference, userTimezone }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isModifyEnabled, setIsModifyEnabled] = useState(false);
  const [BatteryMinCharge, setBatteryMinCharge] = useState(customPreference?.preferences?.min_energy ? customPreference.preferences.min_energy / 1000 : 0.0);
  const [scheduleValues, setScheduleValues] = useState(() => Array(24).fill(0));
  const timeout = parseInt(window.appConfig?.universalTimeout);
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  // compute rounded timezone diff (hours) from userTimezone
  const computeRoundedDiff = (tz) => {
    try {
      const offsetMinutes = DateTime.now().setZone(tz).offset; // minutes east of UTC
      const absMinutes = Math.abs(offsetMinutes);
      const baseHours = Math.floor(absMinutes / 60);
      const rem = absMinutes % 60;
      const roundedAbsHours = rem >= 30 ? baseHours + 1 : baseHours;
      return offsetMinutes >= 0 ? roundedAbsHours : -roundedAbsHours;
    } catch (e) {
      return 0;
    }
  };

  const tzDiff = computeRoundedDiff(userTimezone); // integer hours shift

  useEffect(() => {
    // read server schedule (UTC-indexed) and rotate it into local UI order
    const defaults = Array(24).fill(0);
    try {
      const prefs = customPreference?.preferences;
      if (prefs && Array.isArray(prefs.custom_schedule) && prefs.custom_schedule.length === 24) {
        const serverArr = prefs.custom_schedule;
        // serverArr[i] corresponds to UTC hour i.
        // We want display[(i + diff) % 24] = serverArr[i].
        for (let i = 0; i < 24; i++) {
          const raw = Number(serverArr[i]);
          const value = Number.isFinite(raw) ? raw / 1000 : 0;
          const idx = (i + tzDiff + 24) % 24;
          defaults[idx] = value;
        }
      }
    } catch {
      // fall back to zeros
    }
    setScheduleValues(defaults);
    setBatteryMinCharge(customPreference?.preferences?.min_energy ? customPreference.preferences.min_energy / 1000 : 0.0);
  }, [customPreference, tzDiff]);

  const handleSliderChange = (index, raw) => {
    const val = parseFloat(raw) || 0;
    setScheduleValues((prev) => {
      const copy = prev.slice();
      copy[index] = val;
      return copy;
    });
  };

  const negativeMax = electricalSystem?.bess_max_charge_power ? -electricalSystem.bess_max_charge_power / 1000 : 0.0;
  const positiveMax = electricalSystem?.bess_max_discharge_power ? electricalSystem.bess_max_discharge_power / 1000 : 0.0;
  const sliderStep = 0.01;

  const BatterySize = electricalSystem?.bess_nominal_capacity ? electricalSystem.bess_nominal_capacity / 1000 : t("N/A");
  const BatteryMaxChargePower = electricalSystem?.bess_max_charge_power ? electricalSystem.bess_max_charge_power / 1000 : t("N/A");
  const BatteryMaxDischargePower = electricalSystem?.bess_max_discharge_power ? electricalSystem.bess_max_discharge_power / 1000 : t("N/A");
  const BatteryMinChargePercent = (BatteryMinCharge / BatterySize) * 100;

  const saveMutation = useMutation({
    mutationFn: updateCustomSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["customer_blob"],
        refetchType: "all",
      });
      setIsModifyEnabled(false);
    },
    onError: (err) => {
      console.error("Could not save custom schedule:", err);
      setIsModifyEnabled(false);
      setModalOpen(true);
    },
  });

  const handleSave = () => {
    if (!customPreference?.id) return;
    const prefId = customPreference.id;

    // server expects UTC order: serverArr[utcHour] = scheduleValues[(utcHour + diff) % 24]
    const serverSchedule = Array(24).fill(0);
    for (let k = 0; k < 24; k++) {
      const fromIndex = (k + tzDiff + 24) % 24;
      serverSchedule[k] = Number(scheduleValues[fromIndex]) * 1000;
    }

    const body = {
      min_energy: Number(BatteryMinCharge) * 1000,
      custom_schedule: serverSchedule,
    };

    saveMutation.mutate({
      queryKey: ["custom-schedule", prefId, body, timeout],
    });
  };

  return (
    <Col className="px-0">
      <Card className="mb-3 shadow border-lt">
        <CardBody className="p-2 cardbody-md-default">
          <div
            className="d-flex flex-row justify-content-between align-items-center"
            style={{ minHeight: 60 }}
          >
            <div className="flex-fill text-center">
              <h5 className="text-left text-muted">
                <strong>{t("Geek-corner-expert-mode-settings") + " " + site.display_name}</strong>
              </h5>
            </div>

            <div
              className="status-cell status-cell-4 flex-shrink-0 ms-2"
              style={{ minHeight: 60 }}
            >
              <Button
                color="link"
                onClick={() => setIsOpen(!isOpen)}
                className="status-chevron"
                aria-expanded={isOpen}
              >
                {isOpen ? (
                  <ChevronUp data-testid="geek-corner-chevron-up" size={18} />
                ) : (
                  <ChevronDown
                    data-testid="grrk-corner-chevron-down"
                    size={18}
                  />
                )}
              </Button>
            </div>
          </div>
          <Collapse isOpen={isOpen}>
            <div className="details-row">
              {customPreference
                ?
                <>
                  <div data-testid="ems-info" className="mb-3 ems">
                    <div className="ems-info">
                      <span className="ems-info-icon" aria-hidden>
                        i
                      </span>
                      <span className="ml-3 mr-3">
                        {t("Geek-corner-expert-mode-settings-info")}
                      </span>
                    </div>
                  </div>

                  <div data-testid="ems-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', padding: '1rem', border: "1px solid #d9d9d9", borderRadius: "6px" }}>
                    <div className="ems-actions-text">
                      <span style={{ fontWeight: 'bold' }}>
                        {t("Geek-corner-expert-mode-schedule-for-the-battery")}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button data-testid="modify-button" className="ems-button" onClick={() => setIsModifyEnabled(!isModifyEnabled)}>
                        {t("Geek-corner-expert-mode-modify-button")}
                      </button>
                      <button
                        data-testid="save-button"
                        className="ems-button"
                        onClick={handleSave}
                        disabled={!isModifyEnabled || saveMutation.isLoading}
                      >
                        {t("Geek-corner-expert-mode-save-button")}
                      </button>
                    </div>
                  </div>

                  <div data-testid="ems-min-battery" className="battery-table">
                    <div className="cell cell--label">
                      <span>{t("Geek-corner-expert-mode-battery-minimum-charge")}</span>
                    </div>
                    <div className="cell">
                      <input
                        type="text"
                        value={BatteryMinCharge}
                        onChange={(e) => {
                          let value = e.target.value;
                          value = value.replace(/\D/g, "");
                          if (value.length > 1 && value.startsWith("0")) {
                            value = value.replace(/^0+/, "");
                          }
                          setBatteryMinCharge(value);
                        }}
                        disabled={!isModifyEnabled}
                        className="val1-input"
                      />
                    </div>
                    <div className="cell">kWh</div>
                    <div className="cell">{BatteryMinChargePercent}</div>
                    <div className="cell">%</div>
                  </div>

                  <div data-testid="ems-schedule" className="schedule-table">
                    {Array.from({ length: 24 }).map((_, hour) => {
                      const label = `${String(hour).padStart(2, "0")}:00 - ${String(hour).padStart(2, "0")}:59`;
                      const value = scheduleValues[hour] ?? 0;
                      const disabled = !isModifyEnabled || !electricalSystem;
                      const displayMode = Number(value) > 0 ? t("Discharge") : t("Charge");
                      const absValue = Math.abs(Number(value)).toFixed(2);

                      // compute zero tick percentage on the slider track
                      const min = negativeMax;
                      const max = positiveMax;
                      const range = max - min;
                      const zeroPercent = range > 0 ? ((0 - min) / range) * 100 : 0;

                      return (
                        <div key={hour} data-testid={`ems-schedule-row-${hour}`} className="schedule-row">

                          <div className="cell cell--label" style={{ width: "30%", border: "none", padding: "0 0.5rem" }}>
                            <span>{label}</span>
                          </div>

                          <div
                            className="cell"
                            style={{
                              width: "50%",
                              border: "none",
                              padding: "0 0.5rem",
                              position: "relative",
                              boxSizing: "border-box",
                            }}
                          >
                            {/* zero tick marker */}
                            {range > 0 && (
                              <div
                                aria-hidden
                                style={{
                                  position: "absolute",
                                  left: `${zeroPercent}%`,
                                  top: "-8px",
                                  transform: "translateX(-50%)",
                                  width: "1px",
                                  height: "12px",
                                  backgroundColor: "#333",
                                  pointerEvents: "none",
                                }}
                              />
                            )}

                            <input
                              type="range"
                              min={min}
                              max={max}
                              step={sliderStep}
                              value={value}
                              disabled={disabled}
                              onChange={(e) => handleSliderChange(hour, e.target.value)}
                              style={{ width: "100%", border: "none", background: "transparent" }}
                              aria-label={`schedule-slider-${hour}`}
                              data-testid={`ems-schedule-slider-${hour}`}
                            />
                          </div>

                          <div
                            className="cell"
                            style={{
                              width: "20%",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              border: "none",
                              padding: "0 0.5rem",
                            }}
                          >
                            <div
                              style={{ fontSize: "0.9rem", fontWeight: "600" }}
                              data-testid={`ems-schedule-mode-${hour}`}
                            >
                              {displayMode}
                            </div>
                            <div
                              style={{ fontSize: "0.85rem", color: "var(--brand-navy)" }}
                              data-testid={`ems-schedule-value-${hour}`}
                            >
                              {absValue} kW
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div data-testid="ems-characteristics" className="characteristics-table">
                    <div className="characteristics-cell">
                      <span style={{ fontWeight: 'bold' }}>
                        {t("Geek-corner-expert-mode-site-characteristics")}
                      </span>
                    </div>
                    <div className="characteristics-cell">
                      <span>
                        {t("Geek-corner-expert-mode-battery-size")}
                      </span>
                    </div>
                    <div className="characteristics-cell">{BatterySize}</div>
                    <div className="characteristics-cell">kWh</div>
                    <div className="characteristics-cell">
                      <span>
                        {t("Geek-corner-expert-mode-battery-maximum-charge-power")}
                      </span>
                    </div>
                    <div className="characteristics-cell">{BatteryMaxChargePower}</div>
                    <div className="characteristics-cell">kW</div>
                    <div className="characteristics-cell">
                      <span>
                        {t("Geek-corner-expert-mode-battery-maximum-discharge-power")}
                      </span>
                    </div>
                    <div className="characteristics-cell">{BatteryMaxDischargePower}</div>
                    <div className="characteristics-cell">kW</div>
                  </div>
                </>
                :
                <div data-testid="ems-no-pref-warning" className="mb-3 ems">
                  <div className="ems-info">
                    <span className="ems-info-icon" aria-hidden>
                      i
                    </span>
                    <span className="ml-3 mr-3">
                      {t("Geek-corner-expert-mode-settings-no-pref")}
                    </span>
                  </div>
                </div>
              }
            </div>
          </Collapse>
        </CardBody>
      </Card>

      <ConfirmationModal
        isOpen={modalOpen}
        title={t("Geek-corner-expert-mode-modal-title")}
        message={t("Geek-corner-expert-mode-modal-body")}
        onConfirm={() => {
          setModalOpen(false);
        }}
        onCancel={() => {
          setModalOpen(false);
        }}
        confirmLabel={
          t("Geek-corner-expert-mode-ok")
        }
        cancelLabel={
          t("Geek-corner-expert-mode-ok")
        }
        isError={true}
        stylingPreset={"SteeringOverviewBlock-Optischedule"}
      />

    </Col>
  );
};


const Advanced = () => {
  const data = useAppData();

  return (
    <Container className="mb-2 mb-md-5 px-1 px-md-3">
      <h4 className="mb-2 text-muted"><strong>{data.customer.display_name}</strong></h4>
      <Row style={{ marginInline: 0 }}>
        {data.sites.slice().sort((a, b) => a.id.localeCompare(b.id)).map((site, index) => {

          const scheduleData = site.schedule ? site.schedule : [];
          const loadData = site.load ? site.load : [];
          const entsoeData = site.prices ? site.prices : [];
          const co2Data = site.carbon_intensity ? site.carbon_intensity : [];
          const gridData = site.grid ? site.grid : [];
          const solarData = site.solar ? site.solar : [];
          const elec = data.electrical_systems.find((e) => e.site_id === site.id);
          const customPreference = data.steering_preferences?.find(
            (p) => p.site_id === site.id && p.preference_type === "Custom"
          );

          return (
            <Col key={index} md={10} className="mb-5" style={{ paddingInline: 0 }}>
              <SiteSchedule
                key={index}
                site={{ ...site, customer_display_name: data.customer.display_name }}
                batteryPower={scheduleData}
                entsoePrices={entsoeData}
                carbon_intensity={co2Data}
                grid={gridData}
                load={loadData}
                solar={solarData}
                userTimezone={data.user.timezone}
              />
              <PerformanceOverviewBlock
                key={index + "PerformanceOverview"}
                site={site}
                userTimezone={data.user.timezone}
                showKpiModeSelector={true}
              />
              <ExpertModeSettings
                key={index + "ExpertModeSettings"}
                site={site}
                electricalSystem={elec}
                customPreference={customPreference}
                userTimezone={data.user.timezone}
              />
            </Col>
          );
        })}
      </Row>
    </Container>
  );
};

export default Advanced;
